import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Flame, Plus, Trash2, Loader2, Feather, Volume2 } from "lucide-react";
import { fetchAncestors, addAncestor, deleteAncestor } from "@/lib/api";
import { playBlessing } from "@/lib/blessing";

const ease = [0.22, 1, 0.36, 1];

const AltarCard = ({ ancestor, onDelete }) => {
  const [playing, setPlaying] = useState(false);

  const hear = async () => {
    setPlaying(true);
    try {
      const audio = await playBlessing(ancestor.id);
      audio.addEventListener("ended", () => setPlaying(false));
    } catch (e) {
      setPlaying(false);
      toast.error("The blessing could not be heard just now.");
    }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.5, ease }}
      className="group relative flex flex-col rounded-2xl border border-[#C5A059]/15 bg-gradient-to-b from-[#0b0912] to-[#070707] p-6"
      data-testid="ancestor-card"
    >
      <div className="flex items-start justify-between">
        <Flame size={20} className="text-[#C5A059] [filter:drop-shadow(0_0_10px_rgba(197,160,89,0.6))]" />
        <button
          onClick={() => onDelete(ancestor.id)}
          className="text-white/20 opacity-0 transition-opacity hover:text-[#a35] group-hover:opacity-100"
          title="Release from the altar"
          data-testid={`ancestor-delete-${ancestor.id}`}
        >
          <Trash2 size={15} />
        </button>
      </div>
      <h4 className="mt-4 font-serif text-2xl font-light text-white">{ancestor.name}</h4>
      {(ancestor.relation || ancestor.origin) && (
        <p className="mt-1 text-[0.7rem] uppercase tracking-[0.14em] text-[#C5A059]/80">
          {[ancestor.relation, ancestor.origin].filter(Boolean).join(" · ")}
        </p>
      )}
      {ancestor.note && (
        <p className="mt-3 text-sm font-light italic leading-relaxed text-white/55">“{ancestor.note}”</p>
      )}
      {ancestor.blessing && (
        <p className="mt-4 border-l border-[#C5A059]/25 pl-3 text-xs font-light leading-relaxed text-white/45">
          {ancestor.blessing}
        </p>
      )}
      <button
        onClick={hear}
        disabled={playing}
        className="mt-5 inline-flex items-center gap-2 self-start rounded-full border border-[#C5A059]/40 px-4 py-2 text-[0.6rem] font-light uppercase tracking-[0.2em] text-[#C5A059] transition-colors duration-300 hover:bg-[#C5A059] hover:text-[#050505] disabled:opacity-60"
        data-testid={`ancestor-voice-${ancestor.id}`}
      >
        {playing ? <Loader2 size={13} className="animate-spin" /> : <Volume2 size={13} />}
        {playing ? "Speaking…" : "Hear her blessing"}
      </button>
    </motion.div>
  );
};

export const AncestorAltar = () => {
  const [ancestors, setAncestors] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [form, setForm] = useState({ name: "", relation: "", origin: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    try {
      setAncestors(await fetchAncestors());
    } catch (e) {} finally {
      setLoaded(true);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name the one you honour.");
    setBusy(true);
    try {
      const created = await addAncestor({
        name: form.name.trim(),
        relation: form.relation.trim(),
        origin: form.origin.trim(),
        note: form.note.trim(),
      });
      setAncestors((a) => [...a, created]);
      setForm({ name: "", relation: "", origin: "", note: "" });
      setShowForm(false);
      toast.success(`${created.name} takes their place on your altar.`);
    } catch (err) {
      toast.error("The altar did not receive them. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    const prev = ancestors;
    setAncestors((a) => a.filter((x) => x.id !== id));
    try {
      await deleteAncestor(id);
    } catch (err) {
      setAncestors(prev);
      toast.error("Could not release them from the altar.");
    }
  };

  return (
    <div className="mt-24" data-testid="ancestor-altar">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline mb-6">The Ancestor Altar — Your Lineage</p>
          <h3 className="font-serif text-[clamp(1.6rem,4vw,3rem)] font-light tracking-tight text-white">
            Name those whose blood you <span className="italic text-[#C5A059]">carry</span>.
          </h3>
        </div>
        <p className="max-w-xs text-sm font-light leading-relaxed text-white/45">
          The women and elders you honour here become the root of your authority — the Council weaves
          your named lineage into every governance reading.
        </p>
      </div>

      <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        <AnimatePresence>
          {ancestors.map((a) => (
            <AltarCard key={a.id} ancestor={a} onDelete={remove} />
          ))}
        </AnimatePresence>

        {/* Add tile */}
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="flex min-h-[160px] flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-[#C5A059]/30 text-[#C5A059]/70 transition-colors duration-300 hover:border-[#C5A059]/70 hover:text-[#C5A059]"
            data-testid="ancestor-add-tile"
          >
            <Plus size={24} strokeWidth={1.3} />
            <span className="text-xs uppercase tracking-[0.2em]">Honour an ancestor</span>
          </button>
        )}
      </div>

      {loaded && ancestors.length === 0 && !showForm && (
        <p className="mt-6 flex items-center gap-2 text-sm font-light italic text-white/40">
          <Feather size={15} /> Your altar is unlit. Name your first ancestor to kindle it.
        </p>
      )}

      <AnimatePresence>
        {showForm && (
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.5, ease }}
            className="overflow-hidden"
          >
            <div className="glass mt-6 rounded-3xl p-8" data-testid="ancestor-form">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <label className="block">
                  <span className="overline mb-2 block text-[0.6rem]">Name *</span>
                  <input
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Her name"
                    className="w-full rounded-xl border border-[#C5A059]/25 bg-black/40 px-4 py-3 text-sm text-white/90 outline-none placeholder:text-white/25 focus:border-[#C5A059]/70"
                    data-testid="ancestor-name-input"
                  />
                </label>
                <label className="block">
                  <span className="overline mb-2 block text-[0.6rem]">Relation</span>
                  <input
                    value={form.relation}
                    onChange={set("relation")}
                    placeholder="Grandmother, great-aunt…"
                    className="w-full rounded-xl border border-[#C5A059]/25 bg-black/40 px-4 py-3 text-sm text-white/90 outline-none placeholder:text-white/25 focus:border-[#C5A059]/70"
                    data-testid="ancestor-relation-input"
                  />
                </label>
                <label className="block">
                  <span className="overline mb-2 block text-[0.6rem]">Lineage / origin</span>
                  <input
                    value={form.origin}
                    onChange={set("origin")}
                    placeholder="People, land or nation"
                    className="w-full rounded-xl border border-[#C5A059]/25 bg-black/40 px-4 py-3 text-sm text-white/90 outline-none placeholder:text-white/25 focus:border-[#C5A059]/70"
                    data-testid="ancestor-origin-input"
                  />
                </label>
                <label className="block">
                  <span className="overline mb-2 block text-[0.6rem]">Offering / note</span>
                  <input
                    value={form.note}
                    onChange={set("note")}
                    placeholder="A word of honour"
                    className="w-full rounded-xl border border-[#C5A059]/25 bg-black/40 px-4 py-3 text-sm text-white/90 outline-none placeholder:text-white/25 focus:border-[#C5A059]/70"
                    data-testid="ancestor-note-input"
                  />
                </label>
              </div>
              <div className="mt-7 flex gap-4">
                <button
                  type="submit"
                  disabled={busy}
                  className="inline-flex items-center gap-3 rounded-full border border-[#C5A059] bg-[#C5A059] px-8 py-3.5 text-xs font-medium uppercase tracking-[0.22em] text-[#050505] transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
                  data-testid="ancestor-submit"
                >
                  {busy ? <Loader2 size={15} className="animate-spin" /> : <Flame size={15} />}
                  {busy ? "Lighting the candle…" : "Place on the altar"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="rounded-full border border-[#C5A059]/30 px-6 py-3.5 text-xs uppercase tracking-[0.2em] text-white/60 transition-colors hover:border-[#C5A059]/60"
                  data-testid="ancestor-cancel"
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AncestorAltar;
